import {
  ApplicationConfig,
  provideZoneChangeDetection,
  isDevMode,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouter } from '@angular/router';
import { provideI18n } from '@core/i18n/i18n.config';
import { acceptLanguageInterceptor } from '@core/interceptors/accept-language.interceptor';
import { apiResponseInterceptor } from '@core/interceptors/api-response.interceptor';
import { errorInterceptor } from '@core/interceptors/error.interceptor';
import { authInterceptor } from '@core/interceptors/auth.interceptor';

import { routes } from './app.routes';
import { dashboardFeature } from './dashboard/store/dashboard.reducer';
import { DashboardEffects } from './dashboard/store/dashboard.effects';
import { AccountSecurityEffects } from './dashboard/pages/account-settings/account-security/store/account-security.effects';
import { accountSecurityFeature } from './dashboard/pages/account-settings/account-security/store/account-security.reducer';
import { ActiveDevicesEffects } from './dashboard/pages/account-settings/active-devices/store/active-devices.effects';
import { UserActivityEffects } from './dashboard/pages/user-activity/store/user-activity.effects';
import { userActivityFeature } from './dashboard/pages/user-activity/store/user-activity.reducer';
import { activeDevicesFeature } from './dashboard/pages/account-settings/active-devices/store/active-devices.reducer';
import { provideAnimations } from '@angular/platform-browser/animations';
import { policyReducer } from './dashboard/pages/laws-and-policies/policies/store/policy.reducer';
import { PolicyEffects } from './dashboard/pages/laws-and-policies/policies/store/policy.effects';
import {
  personalInfoReducer,
  personalInfoFeatureKey,
} from './dashboard/pages/account-settings/personal-info/store/personal-info.reducer';
import { PersonalInfoEffects } from './dashboard/pages/account-settings/personal-info/store/personal-info.effects';
import { userAccessManagementFeature } from './dashboard/pages/security-settings/user-access-management/store/user-access-management.reducer';
import { UserAccessManagementEffects } from './dashboard/pages/security-settings/user-access-management/store/user-access-management.effects';
import { accountActivationFeature } from './auth/pages/account-activation/store/account-activation.reducer';
import { AccountActivationEffects } from './auth/pages/account-activation/store/account-activation.effects';
import { loginFeature } from './auth/pages/login/store/login.reducer';
import { LoginEffects } from './auth/pages/login/store/login.effects';
import { passwordRecoveryFeature } from './auth/pages/password-recovery/store/password-recovery.reducer';
import { PasswordRecoveryEffects } from './auth/pages/password-recovery/store/password-recovery.effects';
import {
  userListReducer,
  USER_LIST_FEATURE_KEY,
} from './dashboard/pages/users/store/users.reducer';
import { UserListEffects } from './dashboard/pages/users/store/users.effects';
import { emergencyControlFeature } from './dashboard/pages/security-settings/emergency-control/store/emergency-control.reducer';
import { EmergencyControlEffects } from './dashboard/pages/security-settings/emergency-control/store/emergency-control.effects';
import { assetManagementReducer } from './dashboard/pages/asset-management/shared/store/asset-management.reducer';
import { AssetManagementEffects } from './dashboard/pages/asset-management//shared/store/asset-management.effects';
import { whitelistAddressReducer } from './dashboard/pages/asset-management/whitelist-addresses/store/whitelist-address.reducer';
import { WhitelistAddressEffects } from './dashboard/pages/asset-management/whitelist-addresses/store/whitelist-address.effects';
import { vaultReducer } from './dashboard/pages/asset-management/vaults/store/vault.reducer';
import { VaultEffects } from './dashboard/pages/asset-management/vaults/store/vault.effects';

import { walletReducer } from './dashboard/pages/asset-management/wallets/store/wallet.reducer';
import { WalletEffects } from './dashboard/pages/asset-management/wallets/store/wallet.effects';
import {
  transactionReducer,
  TRANSACTION_FEATURE_KEY,
} from './dashboard/pages/transactions/store/transaction.reducer';
import { TransactionEffects } from './dashboard/pages/transactions/store/transaction.effects';
import {
  SHARED_WALLET_FEATURE_KEY,
  sharedWalletReducer,
} from './shared/store/wallet/wallet.reducer';
import { SharedWalletEffects } from './shared/store/wallet/wallet.effects';
import { SharedVaultEffects } from './shared/store/vault/vault.effects';
import { sharedVaultReducer } from './shared/store/vault/vault.reducer';
import {
  VERIFICATION_FEATURE_KEY,
  verificationReducer,
} from './shared/store/verification/verification.reducer';
import { VerificationEffects } from './shared/store/verification/verification.effects';
import {
  REALTIME_FEATURE_KEY,
  realtimeReducer,
} from './shared/store/realtime/realtime.reducer';
import {
  RealtimeDispatcherEffects,
  RealtimeEffects,
} from './shared/store/realtime/realtime.effects';
import {
  NOTIFICATIONS_FEATURE_KEY,
  notificationsReducer,
} from './shared/store/notifications/notifications.reducer';
import { NotificationsEffects } from './shared/store/notifications/notifications.effects';
import { AuthEffects } from './auth/store/auth.effects';
import { clearStateOnSignOut } from './auth/store/clear-state-on-sign-out.meta-reducer';
import {
  APPROVAL_FEATURE_KEY,
  approvalReducer,
} from './dashboard/pages/laws-and-policies/approvals/store/approval.reducer';
import { ApprovalEffects } from './dashboard/pages/laws-and-policies/approvals/store/approval.effects';

/**
 * The root slices. Exported so the sign-out spec can drive every one of them
 * through `ROOT_META_REDUCERS` rather than a hand-kept list.
 */
export const ROOT_REDUCERS = {
  policy: policyReducer,
  accountSecurity: accountSecurityFeature.reducer,
  [activeDevicesFeature.name]: activeDevicesFeature.reducer,
  [userActivityFeature.name]: userActivityFeature.reducer,
  [personalInfoFeatureKey]: personalInfoReducer,
  [userAccessManagementFeature.name]: userAccessManagementFeature.reducer,
  [dashboardFeature.name]: dashboardFeature.reducer,
  [accountActivationFeature.name]: accountActivationFeature.reducer,
  [loginFeature.name]: loginFeature.reducer,
  [passwordRecoveryFeature.name]: passwordRecoveryFeature.reducer,
  [USER_LIST_FEATURE_KEY]: userListReducer,
  whitelistAddress: whitelistAddressReducer,
  vault: vaultReducer,
  [emergencyControlFeature.name]: emergencyControlFeature.reducer,
  wallet: walletReducer,
  [TRANSACTION_FEATURE_KEY]: transactionReducer,
  [SHARED_WALLET_FEATURE_KEY]: sharedWalletReducer,
  sharedVaultReducer: sharedVaultReducer,
  assetManagementReducer: assetManagementReducer,
  [VERIFICATION_FEATURE_KEY]: verificationReducer,
  [REALTIME_FEATURE_KEY]: realtimeReducer,
  [NOTIFICATIONS_FEATURE_KEY]: notificationsReducer,
  [APPROVAL_FEATURE_KEY]: approvalReducer,
};

/** Signing out returns every slice above to its initial state (F-031). */
export const ROOT_META_REDUCERS = [clearStateOnSignOut];

export const appConfig: ApplicationConfig = {
  providers: [
    // Interceptor order matters. Angular runs them first-to-last on the way
    // out to the backend, and last-to-first on the way back. The order
    // below means a response flows: authInterceptor (401/refresh+retry) →
    // errorInterceptor (toast errors) → apiResponseInterceptor (toast
    // localized success messages from the envelope) → acceptLanguageInterceptor
    // (request-only). Errors re-thrown by errorInterceptor bypass
    // apiResponseInterceptor's `tap`, so no double-toasting.
    provideI18n(),
    provideHttpClient(
      withInterceptors([
        acceptLanguageInterceptor,
        apiResponseInterceptor,
        errorInterceptor,
        authInterceptor,
      ])
    ),
    provideZoneChangeDetection(),
    provideRouter(routes),
    provideAnimations(),
    provideStore(ROOT_REDUCERS, { metaReducers: ROOT_META_REDUCERS }),
    provideEffects([
      PolicyEffects,
      AccountSecurityEffects,
      ActiveDevicesEffects,
      UserActivityEffects,
      PersonalInfoEffects,
      DashboardEffects,
      UserAccessManagementEffects,
      AccountActivationEffects,
      LoginEffects,
      PasswordRecoveryEffects,
      UserListEffects,
      WhitelistAddressEffects,
      VaultEffects,
      EmergencyControlEffects,
      WalletEffects,
      TransactionEffects,
      SharedWalletEffects,
      SharedVaultEffects,
      AssetManagementEffects,
      VerificationEffects,
      AuthEffects,
      RealtimeEffects,
      RealtimeDispatcherEffects,
      NotificationsEffects,
      ApprovalEffects,
    ]),
    // Redux DevTools only in development builds (decision D7, F-035): the
    // login slice holds the operator's password during the second-factor step
    // until user-management's resend endpoint lands, and an instrumented store
    // in production let a DevTools extension read it.
    isDevMode()
      ? provideStoreDevtools({
          maxAge: 25,
          logOnly: false,
          trace: false,
          traceLimit: 75,
        })
      : [],
  ],
};
